import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  listCurators,
  assignCurator,
  removeCurator,
  listAssignablePhotographers,
  assignCuratorById,
  type CuratorEntry,
  type AssignablePhotographer,
} from "@/lib/roles.functions";

export const Route = createFileRoute("/admin/roles")({
  component: RolesPage,
});

function RolesPage() {
  const [items, setItems] = useState<CuratorEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [photographers, setPhotographers] = useState<AssignablePhotographer[]>([]);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);
  const [photoLoading, setPhotoLoading] = useState(true);

  async function load() {
    setLoading(true);
    setPhotoLoading(true);
    try {
      setItems(await listCurators());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Konnte Kurator:innen nicht laden.");
    } finally {
      setLoading(false);
    }
    try {
      setPhotographers(await listAssignablePhotographers());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Konnte Fotograf:innen nicht laden.");
    } finally {
      setPhotoLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleAssign(e: React.FormEvent) {
    e.preventDefault();
    const value = email.trim();
    if (!value) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const entry = await assignCurator({ data: { email: value } });
      setEmail("");
      setNotice(`${entry.email} ist jetzt Kurator:in.`);
      await load();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Zuweisung fehlgeschlagen.";
      setError(
        msg.includes("404") || msg.toLowerCase().includes("kein account")
          ? "Es existiert kein Account mit dieser E-Mail."
          : msg,
      );
    } finally {
      setBusy(false);
    }
  }

  async function doRemove(userId: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await removeCurator({ data: { userId } });
      setConfirmRemoveId(null);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Entfernen fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  async function handleAssignPhotographer(p: AssignablePhotographer) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await assignCuratorById({ data: { userId: p.userId } });
      setNotice(`${p.name || p.email || p.userId} ist jetzt Kurator:in.`);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Zuweisung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="eyebrow text-muted-foreground">Rollen</div>
      <h1 className="mt-3 text-2xl font-medium">Kurator:innen</h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Weise registrierten Personen die Rolle Kurator:in zu. Die Person muss bereits
        einen Account haben (gleiche E-Mail-Adresse).
      </p>

      <form onSubmit={handleAssign} className="mt-8 flex flex-wrap items-center gap-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="E-Mail-Adresse"
          required
          className="min-w-[260px] flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
        />
        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-foreground px-4 py-2 text-sm text-background disabled:opacity-50"
        >
          Zuweisen
        </button>
      </form>

      {error && <p className="mt-4 text-sm text-destructive">{error}</p>}
      {notice && <p className="mt-4 text-sm text-muted-foreground">{notice}</p>}

      <div className="mt-12">
        <div className="eyebrow text-muted-foreground">Verfügbare Fotograf:innen</div>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Fotograf:innen mit verknüpftem Account, denen du die Rolle Kurator:in direkt
          zuweisen kannst, ohne eine E-Mail einzugeben.
        </p>
        <div className="mt-4">
          {photoLoading ? (
            <p className="text-sm text-muted-foreground">Lädt …</p>
          ) : photographers.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Keine Fotograf:innen mit verknüpftem Account gefunden.
            </p>
          ) : (
            <ul className="divide-y divide-border border-t border-border">
              {photographers.map((p) => (
                <li key={p.userId} className="flex items-center justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{p.name || p.userId}</div>
                    {p.email && (
                      <div className="truncate text-xs text-muted-foreground">{p.email}</div>
                    )}
                  </div>
                  {p.isCurator ? (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      Bereits Kurator:in
                    </span>
                  ) : (
                    <button
                      onClick={() => handleAssignPhotographer(p)}
                      disabled={busy}
                      className="shrink-0 rounded-md border border-border px-3 py-1.5 text-xs hover:bg-muted disabled:opacity-50"
                    >
                      Freigeben
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-12">
        <div className="eyebrow text-muted-foreground">Aktuelle Kurator:innen</div>
        <div className="mt-4" />
        {loading ? (
          <p className="text-sm text-muted-foreground">Lädt …</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch keine Kurator:innen.</p>
        ) : (
          <ul className="divide-y divide-border border-t border-border">
            {items.map((c) => (
              <li key={c.userId} className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium">
                    {c.displayName || c.email || c.userId}
                  </div>
                  {c.email && c.displayName && (
                    <div className="truncate text-xs text-muted-foreground">{c.email}</div>
                  )}
                </div>
                <button
                  onClick={() => setConfirmRemoveId(c.userId)}
                  disabled={busy}
                  className="shrink-0 text-xs text-muted-foreground hover:text-destructive disabled:opacity-50"
                >
                  Entfernen
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {confirmRemoveId && (
        <div
          className="fixed inset-0 z-50 bg-foreground/40 flex items-center justify-center p-4"
          onClick={() => !busy && setConfirmRemoveId(null)}
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-background border border-border w-full max-w-md p-6">
            <div className="eyebrow text-muted-foreground">Rolle entfernen</div>
            <h2 className="serif text-2xl mt-2">
              {items.find((i) => i.userId === confirmRemoveId)?.displayName ||
               items.find((i) => i.userId === confirmRemoveId)?.email ||
               confirmRemoveId}
            </h2>
            <p className="text-sm text-muted-foreground mt-3">
              Die Kurator:innen-Rolle wird entzogen. Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div className="mt-6 flex flex-wrap gap-2 justify-end">
              <button
                disabled={busy}
                onClick={() => setConfirmRemoveId(null)}
                className="eyebrow border border-border px-4 py-2 disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                disabled={busy}
                onClick={() => doRemove(confirmRemoveId)}
                className="eyebrow bg-destructive text-destructive-foreground px-4 py-2 disabled:opacity-50"
              >
                {busy ? "Entferne …" : "Rolle entfernen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}